import { GraphBuilder } from '../graph/builder';
import { ComponentDef } from './types';

export const attentionSinkComponent: ComponentDef = {
  id: 'attention-sink',
  name: 'Attention Sink',
  description: 'Retain a few initial tokens in KV cache to stabilize attention for very long sequences.',
  category: 'optimization',
  params: [
    { name: 'd_model', label: 'Model dimension', type: 'number', default: 512, min: 64, max: 4096, step: 64 },
    { name: 'seq_len', label: 'Sequence length', type: 'number', default: 4096, min: 1, max: 131072, step: 1 },
    { name: 'sink_len', label: 'Sink tokens', type: 'number', default: 4, min: 1, max: 64, step: 1 },
    {
      name: 'variant',
      label: 'Variant',
      type: 'select',
      default: 'sink_pattern',
      options: [
        { label: 'Sink Pattern', value: 'sink_pattern' },
        { label: 'StreamingLLM', value: 'streaming_llm' },
        { label: 'Heavy Hitters (H2O)', value: 'h2o' }
      ]
    }
  ],
  buildGraph: (params) => {
    const d_model = Number(params.d_model);
    const seq_len = Number(params.seq_len);
    const sink_len = Number(params.sink_len);
    const variant = String(params.variant);

    const g = new GraphBuilder();
    g.input('x', [seq_len, d_model]);

    g.linear('q', 'x', [d_model, d_model]);
    g.linear('k', 'x', [d_model, d_model]);
    g.linear('v', 'x', [d_model, d_model]);

    if (variant === 'streaming_llm') {
      // StreamingLLM: sliding window + sink tokens
      g.cache('kv_full', 'k', [seq_len, d_model]);
      g.scale('sink_tokens', 'kv_full', `sink ${sink_len}`);
      g.scale('window', 'kv_full', `window ${Math.min(seq_len, 512)}`);
      g.concat('kv', ['sink_tokens', 'window'], [sink_len + Math.min(seq_len, 512), d_model]);
      g.matmul('qk', 'q', 'kv', [seq_len, sink_len + Math.min(seq_len, 512)]);
      g.softmax('softmax', 'qk', -1);
      g.cache('v_cache', 'v', [seq_len, d_model]);
      g.scale('v_sink', 'v_cache', `sink ${sink_len}`);
      g.scale('v_window', 'v_cache', `window ${Math.min(seq_len, 512)}`);
      g.concat('v_sel', ['v_sink', 'v_window'], [sink_len + Math.min(seq_len, 512), d_model]);
      g.matmul('out', 'softmax', 'v_sel', [seq_len, d_model]);
    } else if (variant === 'h2o') {
      // Heavy Hitters (H2O): evict based on cumulative attention mass
      g.cache('kv', 'k', [seq_len, d_model]);
      g.matmul('qk', 'q', 'kv', [seq_len, seq_len]);
      g.softmax('attn_scores', 'qk', -1);
      g.scale('cumsum', 'attn_scores', 'Cumulative Mass');
      g.mask('evict_mask', 'cumsum', [seq_len, seq_len]);
      g.mul('attn_masked', 'attn_scores', 'evict_mask');
      g.matmul('out', 'attn_masked', 'v', [seq_len, d_model]);
    } else {
      // Sink Pattern (default): keep first N tokens
      g.cache('kv', 'k', [seq_len, d_model]);
      g.scale('sink', 'kv', `sink ${sink_len}`);
      g.matmul('qk', 'q', 'sink', [seq_len, seq_len]);
      g.softmax('softmax', 'qk', -1);
      g.matmul('out', 'softmax', 'v', [seq_len, d_model]);
    }

    g.output('out', 'out');
    return g.build();
  }
};
